import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { setToken, getToken } from "@/lib/api";
import { gaEvent } from "@/lib/analytics";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=guest, obj=user

  const refresh = useCallback(async () => {
    const tokenAtStart = getToken();
    if (!tokenAtStart) {
      setUser(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      // Only clear if the token that failed is STILL the current one. This
      // prevents a stale in-flight /auth/me from wiping a fresher session
      // (e.g. a login that completed while this request was in flight).
      if (getToken() === tokenAtStart) {
        setToken(null);
        setUser(false);
      }
    }
  }, []);

  useEffect(() => {
    // Only defer to AuthCallback on its dedicated route. A stray "#session_id="
    // hash on any OTHER route must NOT block auth initialization (that left
    // user === null forever and froze /admin on a spinner).
    if (window.location.pathname === "/auth/callback" && window.location.hash?.includes("session_id=")) {
      return;
    }
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setToken(data.token);
    setUser(data.user);
    gaEvent("login", { method: "password" });
    return data.user;
  };

  const register = async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    setToken(data.token);
    setUser(data.user);
    gaEvent("sign_up", { method: "email" });
    return data.user;
  };

  const googleSession = async (session_id) => {
    const { data } = await api.post("/auth/google/session", { session_id });
    setToken(data.token);
    setUser(data.user);
    gaEvent("login", { method: "google" });
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {}
    setToken(null);
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, register, googleSession, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}
